/* eslint-disable static-service/require-stories -- context provide that is covered by tests*/
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {useTimeout} from "@khanacademy/wonder-blocks-timing";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {a11yStyles} from "@repo/shared-styles";

interface Props {
    /**
     * The id of the table to associate the pagination controls with.
     */
    id: string;
    /**
     * Callback when the page is changed.
     * The number passed in is the new page we expect to navigate to.
     */
    onPageChange: (newPage: number) => void;
    /**
     * The current page.
     */
    page: number;
    /**
     * The number of items per page.
     * If not provided, pagination will not be rendered.
     */
    pageSize: number;
    /**
     * The total number of items to base the pagination on.
     */
    total: number;
    /**
     * Whether to indicate that content is loading.
     */
    loading?: boolean;
    /**
     * Whether there is more data to load past the current total pages
     */
    hasMorePages?: boolean;
}

const strings: Record<string, any> = {
    paginationAnnouncement: (page: number, totalPages: number) =>
        t`Current page: ${page} of ${totalPages}.`,
};

/**
 * Renders a pagination component that allows the user to navigate between
 * pages.
 */
export default function Pagination({
    id,
    page,
    pageSize,
    onPageChange,
    total,
    loading,
    hasMorePages,
}: Props): React.ReactElement | null {
    const totalPages = pageSize ? Math.ceil(total / pageSize) : total;
    const totalText = hasMorePages ? `${totalPages} or more` : totalPages;

    // Delay the announcement of the loading state to avoid announcing it
    // immediately when the page changes.
    const [announceLoading, setAnnounceLoading] = React.useState(false);
    const announce = React.useCallback(() => {
        setAnnounceLoading(true);
    }, []);
    const {clear, set} = useTimeout(announce, 1000);
    React.useEffect(() => {
        if (loading) {
            set();
        } else {
            clear();
        }
        return () => {
            clear();
            setAnnounceLoading(false);
        };
    }, [loading, clear, set]);

    if (!pageSize || totalPages <= 1) {
        return null;
    }

    // This announcement is read by screen readers when the user navigates to
    // a new page.
    const paginationAnnouncementMessage = strings.paginationAnnouncement(
        page,
        totalText,
    );

    return (
        <nav id={id} aria-labelledby={`${id}-caption`}>
            {/**
             * Include a status message to announce to ATs that the page has changed.
             * @see https://www.w3.org/WAI/WCAG22/Techniques/aria/ARIA22
             */}
            <View style={a11yStyles.srOnly} role="status" id={`${id}-caption`}>
                {announceLoading
                    ? t`Loading...`
                    : paginationAnnouncementMessage}
            </View>
            <View style={styles.container}>
                <Button
                    kind="tertiary"
                    disabled={page === 1}
                    onClick={() => {
                        onPageChange(page - 1);
                    }}
                    aria-controls={id}
                    // Used to provide a more descriptive label for screen readers.
                    aria-label={t`previous page`}
                    spinner={loading}
                >
                    {t`Previous`}
                </Button>
                <Strut size={spacing.medium_16} />
                |
                <Strut size={spacing.medium_16} />
                <Button
                    kind="tertiary"
                    disabled={page === totalPages}
                    onClick={() => {
                        onPageChange(page + 1);
                    }}
                    aria-controls={id}
                    // Used to provide a more descriptive label for screen readers.
                    aria-label={t`next page`}
                    spinner={loading}
                >
                    {t`Next`}
                </Button>
            </View>
        </nav>
    );
}

const styles = StyleSheet.create({
    container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
});
